import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDiscardGuard } from '@/components/ui/form-dialog-content'
import { GastoFormFields } from './forms/GastoFormFields'
import { GastoAdjuntos } from './GastoAdjuntos'
import type { GastoFormValues } from './forms/gastoSchema'
import type { MovimientoConCliente } from '@/types/database'

interface GastoFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  gasto?: MovimientoConCliente | null
  onSubmit: (values: GastoFormValues) => Promise<void>
  isSubmitting?: boolean
}

export function GastoFormDialog({
  open,
  onOpenChange,
  gasto,
  onSubmit,
  isSubmitting,
}: GastoFormDialogProps) {
  const isEditing = !!gasto
  const [isFormDirty, setIsFormDirty] = useState(false)

  const { holdClose, confirmation } = useDiscardGuard({
    isDirty: isFormDirty,
    onDiscard: () => onOpenChange(false),
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && holdClose()) return
    onOpenChange(newOpen)
  }

  const handleSubmit = async (values: GastoFormValues) => {
    await onSubmit(values)
    onOpenChange(false)
  }

  const defaultValues: Partial<GastoFormValues> | undefined = gasto
    ? {
        monto: gasto.debe ?? gasto.monto ?? 0,
        concepto: gasto.concepto,
        fecha: gasto.fecha,
        cuenta_id: gasto.cuenta_id ?? '',
        categoria_id: gasto.categoria_id ?? '',
        moneda: (gasto.moneda as 'ARS' | 'USD') ?? 'ARS',
        estado_pago: (gasto.estado_pago as 'pagado' | 'pendiente') ?? 'pagado',
        observaciones: gasto.observaciones,
      }
    : undefined

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Editar' : 'Nuevo'} gasto</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Modificá los datos del gasto del estudio.'
                : 'Registrá un gasto interno del estudio.'}
            </DialogDescription>
          </DialogHeader>
          <GastoFormFields
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            onCancel={() => handleOpenChange(false)}
            onDirtyChange={setIsFormDirty}
            isSubmitting={isSubmitting}
            isEditing={isEditing}
          />
          {/* Comprobantes: solo en edición (el gasto debe existir para vincular adjuntos).
              Se suben de inmediato (no hay selección pendiente), así que no ensucian el form. */}
          {isEditing && gasto && (
            <div className="border-t pt-4">
              <GastoAdjuntos gastoId={gasto.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {confirmation}
    </>
  )
}
